import React, { useContext } from 'react';
import { Accordion, BasicCollapsible, Box, Li, Ul } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { aggregateTaxAwarePrices } from '@vp/om-tax-display';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import PricedItemOption from './PricedItemOption';
import FeeOption from './FeeOption';
import UnpricedItemOption from './UnpricedItemOption';
import {
  DEFAULT_TAX_DISPLAY,
  ITEM_LEVEL,
} from '../../../constants/taxDisplayOptions';

/**
 * Responsible for the display of an item's base price, fees, and priced / unpriced item options.
 * 1. Fetches product pricing data from context
 * 2. Generates the tax aware price for a given pricing component
 * 3. Generates a list of options as a combination of attribute display data and pricing data
 * 4. Displays the option
 *
 * Notes:
 * - we only display ItemSelectedOptions for new platform items
 * - the term "component" refers to the pricing representation for a selectedOption (fee or item option)
 * - the term "attribute" refers to the display object for a selectedOption
 * - expected that this is rendered within an LineItemDataProvider to access item properties
 * - uses om-tax-display library to apply tax display rules (tax-inclusivity and display formatting).
 */
export const ItemSelectedOptions = () => {
  const { id, pricingData, attributes } = useLineItemData();
  const { itemIdToTaxInclusiveAndExclusivePricesMap } =
    useContext(OrderDetailsContext);
  const [lineItemMessages] = useTranslations(['lineItemMessages']);

  const subtotalRequests = [];
  // mark each pricing component with label for subtotaling
  pricingData.components?.forEach(component => {
    subtotalRequests.push({ key: component.name });
  });

  // returns an object with a subtotals list in the form of [{ key: "componentName", value: {tax aware prices for component}]
  const taxAwareItemTotalPrice = aggregateTaxAwarePrices(
    itemIdToTaxInclusiveAndExclusivePricesMap[id],
    ITEM_LEVEL,
    DEFAULT_TAX_DISPLAY,
    subtotalRequests.flat(),
    {} // OrderDetails doesn't respect the VAT display toggle, so we pass an empty object for taxDisplayContext
  );

  // Filter the components to those with prices only
  const pricedComponents = pricingData.components?.filter(
    component =>
      !!(component.grossDiscountedCents && component.grossDiscountedCents > 0)
  );

  // find all options that have display values
  const displayableOptions = attributes.filter(
    attribute => attribute.keyDisplayName && attribute.valueDisplayName
  );

  /*
  - Build the list of selected options (fees, priced items, unpriced items) needed for display.
  - A "selected option" is the combination of attribute pricing and display data

  - build unpricedDisplayAttributes as a subset of displayableOptions excluding options with associated pricing
  - For each priced component:
    - find the displayable option associated with the pricing component (if it exists) and build a pricedOption using a combination of pricing data and display data
    - add the pricedOption to the feeOptions / pricedItemOptions lists and remove that displayable option from the unpricedItemOptions list
  */
  const pricedItemOptions = [];
  let unpricedItemOptions = displayableOptions;
  const feesOptions = [];

  pricedComponents?.forEach(component => {
    // get the name of this pricing component
    const { name } = component;

    // get the tax aware prices for this component
    const taxAwarePrices = taxAwareItemTotalPrice.subtotals.get(name) || {};

    // find corresponding display data for this pricing component
    const optionDisplayData = displayableOptions.find(
      opt => name === `${opt.key}: ${opt.value}`
    );

    // package up all the pricing and display data for this component into a priced display option
    const pricedOption = {
      name,
      listCents: taxAwarePrices.taxAwareListCents,
      discountedCents: taxAwarePrices.taxAwareDiscountedCents,
      ...optionDisplayData,
    };

    // add the priced option to its corresponding list
    if (component.type === 'FEE') {
      feesOptions.push(pricedOption);
    } else {
      pricedItemOptions.push(pricedOption);
    }

    // then remove that displayable option from the unpriced options list
    unpricedItemOptions = unpricedItemOptions.filter(
      value => value !== optionDisplayData
    );
  });

  // only show the selectedOptions UX if at least one displayable option exists
  const showSelectedOptions = displayableOptions.length > 0;

  const hasOptions =
    pricedItemOptions.length > 0 || unpricedItemOptions.length > 0;

  const hasFees = feesOptions.length > 0;

  if (showSelectedOptions && hasOptions) {
    return (
      <Accordion single skin="standard">
        <BasicCollapsible
          heading={lineItemMessages.selectedOptions}
          collapsibleId="selected-option-breakdowns"
          role="note"
          fullBleed
        >
          <Ul skin="minimal">
            {unpricedItemOptions.map(option => (
              <Li key={`unpriced-item-option-${option.key}`}>
                <UnpricedItemOption option={option} />
              </Li>
            ))}
            {pricedItemOptions.map(option => (
              <Li key={`priced-item-option-${option.key}`}>
                <PricedItemOption option={option} />
              </Li>
            ))}
            {hasFees &&
              feesOptions.map(fee => (
                <Li key={`fee-option-${fee.name}`}>
                  <FeeOption fee={fee} />
                </Li>
              ))}
          </Ul>
        </BasicCollapsible>
      </Accordion>
    );
  }
  /* Fixes rendering when no selectedOptions shown */
  return <Box mt={6} />;
};
